export interface ResolvedAddress {
    /** Google's full address, e.g. `12 Herman Ave, Toronto, ON M6R 1X6, Canada`. */
    address: string;
    city: string | null;
    province: string | null;
    postalCode: string | null;
}

function getComponent(place: google.maps.places.PlaceResult, type: string): string | null {
    return (
        place.address_components?.find((component) => component.types.includes(type))?.long_name ??
        null
    );
}

/** Google's full address for a place, with the postal code added when it isn't already in it. */
export function formatPlaceAddress(place: google.maps.places.PlaceResult): string {
    const formattedAddress = place.formatted_address || '';
    const postalCode = getComponent(place, 'postal_code');
    if (!postalCode || formattedAddress.toLowerCase().includes(postalCode.toLowerCase())) {
        return formattedAddress;
    }
    return `${formattedAddress}, ${postalCode}`;
}

async function getPlacePredictions(
    input: string,
    sessionToken: google.maps.places.AutocompleteSessionToken,
): Promise<google.maps.places.AutocompletePrediction[]> {
    try {
        const { predictions } =
            await new google.maps.places.AutocompleteService().getPlacePredictions({
                input,
                types: ['address'],
                componentRestrictions: { country: 'ca' },
                sessionToken,
            });
        return predictions;
    } catch {
        // Google rejects when there are no results (ZERO_RESULTS) as well as on failures.
        return [];
    }
}

/**
 * The place's address only. Pass the session token of the autocomplete requests that found it,
 * so Google bills them together as one lookup.
 */
export function getPlaceDetails(
    placeId: string,
    sessionToken?: google.maps.places.AutocompleteSessionToken,
): Promise<google.maps.places.PlaceResult | null> {
    return new Promise((resolve) => {
        new google.maps.places.PlacesService(document.createElement('div')).getDetails(
            { placeId, fields: ['formatted_address', 'address_components'], sessionToken },
            (place, status) => resolve(status === 'OK' && place ? place : null),
        );
    });
}

function getStreetNumbers(streetLine: string): string[] {
    return streetLine.toLowerCase().match(/\d+[a-z]?/g) ?? [];
}

/**
 * The address Google's autocomplete would give for an address a person didn't pick, such as
 * one read from a document. Null when Google is unavailable or its top match has a different
 * street number, so a vague address is never swapped for the wrong one.
 */
export async function resolveAddress(query: string): Promise<ResolvedAddress | null> {
    const streetLine = query.split(',')[0].trim();
    const queryNumbers = getStreetNumbers(streetLine);
    if (!window.google?.maps?.places || queryNumbers.length === 0) {
        return null;
    }

    try {
        const sessionToken = new google.maps.places.AutocompleteSessionToken();
        const [prediction] = await getPlacePredictions(query, sessionToken);
        const place = prediction ? await getPlaceDetails(prediction.place_id, sessionToken) : null;
        const streetNumber = place ? getComponent(place, 'street_number')?.toLowerCase() : null;
        if (!place || !streetNumber || !queryNumbers.includes(streetNumber)) {
            return null;
        }

        const formatted = formatPlaceAddress(place);
        const [placeStreetLine, ...rest] = formatted.split(',');
        // Google can match the building and drop a unit number, so keep the street line as given.
        const placeNumbers = getStreetNumbers(placeStreetLine);
        const address = queryNumbers.every((number) => placeNumbers.includes(number))
            ? formatted
            : [streetLine, ...rest].join(',');

        return {
            address,
            city: getComponent(place, 'locality') ?? getComponent(place, 'postal_town'),
            province: getComponent(place, 'administrative_area_level_1'),
            postalCode: getComponent(place, 'postal_code'),
        };
    } catch (error) {
        console.error('Address lookup failed:', error);
        return null;
    }
}
